import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Select, IProps as SelectProps } from 'components/atoms/Select/Select.components';
import { useGetPicklistQuery } from 'services/sap';
import { formatCountrySelect } from 'utils/helpers';
import { catalogoPersonal, catalogoLenguage } from 'utils/helpers/dictionaries';

interface CountrySelectProps extends Omit<SelectProps, 'options' | 'handleChange'> {
  onExternalCodeChange?: (externalCode: string) => void;
  name?: any;
  handleChange: (value?: string, externalCode?: string, valueEn?: string) => void;
  defaultValue?: string;
  allowedCountries?: string[];
}

export default function CountrySelect({
  onExternalCodeChange,
  name,
  defaultValue = '',
  allowedCountries,
  ...rest
}: Readonly<CountrySelectProps>) {
  const { getValues } = useFormContext();
  const { data, isLoading } = useGetPicklistQuery<{
    data: { externalCode: string; label_es_ES: string; label_en_US: string }[];
    isLoading: boolean;
  }>({
    catalogName: catalogoPersonal.ISOCountryList,
    languages: catalogoLenguage.ESyUSyISOyId,
  });

  const [initialLoading, setInitialLoading] = useState(false);

  const allOptions = formatCountrySelect({ initialList: data ?? [] });
  const options = allowedCountries
    ? allOptions.filter((opt) => allowedCountries.includes(opt.value))
    : allOptions;

  useEffect(() => {
    const formValue = getValues(name);
    if (!data || isLoading || !defaultValue || initialLoading) {
      return;
    }
    let country;

    if (formValue === defaultValue) {
      country = data?.find((c) => c.label_es_ES === defaultValue);
    }

    if (!country) {
      return;
    }

    if (onExternalCodeChange) {
      onExternalCodeChange(country?.externalCode ?? '');
    }

    rest.handleChange(defaultValue ?? '', country?.externalCode ?? '', country?.label_en_US ?? '');

    setInitialLoading(true);
  }, [isLoading, data]);

  return (
    <Select
      options={options}
      {...rest}
      disabled={rest.disabled || isLoading}
      handleChange={(value) => {
        let country;
        if (value) {
          country = data?.find((country) => country.label_es_ES === value);
        }
        if (onExternalCodeChange) {
          onExternalCodeChange(country?.externalCode ?? '');
        }
        rest.handleChange(
          value?.toString() ?? '',
          country?.externalCode ?? '',
          country?.label_en_US ?? ''
        );
      }}
    />
  );
}
